export interface Notification {
  /**
   * The id of the notification
   * @immutable
   */
  id?: string;
}

export interface NotifyRequest {
  /**
   * Notification template ID. A notification template specifies the text and recipients for notifications.
   * To obtain a notification template ID, create a notification template in your [app's dashboard](https://dev.wix.com/docs/build-apps/develop-your-app/app-dashboard-setup/about-the-app-dashboard).
   * @format GUID
   */
  notificationTemplateId: string;
  /**
   * Each key is a placeholder name you specify when creating a notification template.
   * The value is an object containing the text to replace the placeholder in the notifications.
   */
  dynamicValues?: Record<string, DynamicValue>;
}

export interface DynamicValue extends DynamicValueOfTypeOneOf {
  /** Text to be integrated into the notification. */
  text?: string;
}

/** @oneof */
export interface DynamicValueOfTypeOneOf {
  /** Text to be integrated into the notification. */
  text?: string;
}

/**
 * Money.
 * Default format to use. Sufficiently compliant with majority of standards: w3c, ISO 4217, ISO 20022, ISO 8583:2003.
 */
export interface Money {
  /**
   * Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.
   * @format DECIMAL_VALUE
   */
  value?: string;
  /**
   * Currency code. Must be valid ISO 4217 currency code (e.g., USD).
   * @format CURRENCY
   */
  currency?: string;
  /** Monetary amount. Decimal string in local format (e.g., 1 000,30). Optionally, a single (-), to indicate that the amount is negative. */
  formattedValue?: string | null;
}

export interface MapDynamicValue {
  /** items */
  items?: Record<string, DynamicValue>;
}

export interface ArrayDynamicValue {
  /**
   * items
   * @maxSize 100
   */
  items?: DynamicValue[];
}

export interface AttachmentDynamicValue {
  /** file name */
  fileName?: string;
  /** download url */
  downloadUrl?: string;
}

export interface NotifyResponse {
  /** ID of the batch of notifications triggered by one request. */
  notificationBatchId?: string;
}

export interface NotifyByAppRequest {
  /**
   * notification_template_id
   * @format GUID
   */
  notificationTemplateId?: string;
  /**
   * app_def_id
   * @format GUID
   */
  appDefId?: string;
  /** dynamic_values */
  dynamicValues?: Record<string, DynamicValue>;
  /** template tenant */
  templateTenant?: string | null;
  /** overrides */
  overrides?: Overrides;
  /** channels content */
  channelsData?: ChannelsData;
}

export interface Overrides {
  /**
   * excluded_channels
   * @maxSize 20
   */
  excludedChannels?: TemplateChannelWithLiterals[];
  /** excluded_audiences */
  excludedAudiences?: ExcludedAudiences;
  /** content overrides */
  content?: ChannelsContent;
}

export enum TemplateChannel {
  WEB_FEED = 'WEB_FEED',
  MOBILE_FEED = 'MOBILE_FEED',
  MOBILE_PUSH = 'MOBILE_PUSH',
  BROWSER = 'BROWSER',
  SMS = 'SMS',
  EMAIL = 'EMAIL',
  KAFKA = 'KAFKA',
  VOICE = 'VOICE',
}

/** @enumType */
export type TemplateChannelWithLiterals =
  | TemplateChannel
  | 'WEB_FEED'
  | 'MOBILE_FEED'
  | 'MOBILE_PUSH'
  | 'BROWSER'
  | 'SMS'
  | 'EMAIL'
  | 'KAFKA'
  | 'VOICE';

export interface ExcludedAudiences {
  /**
   * audience_key
   * @maxSize 20
   */
  audienceKey?: string[];
}

export interface ChannelsContent {
  /** mobile push override */
  mobileContent?: MobileContent;
}

export interface MobileContent {
  /** title override of mobile push content */
  title?: string;
  /** body override of mobile push content */
  body?: string;
}

export interface ChannelsData {
  /** email data */
  emailData?: EmailData;
}

export interface EmailData {
  /** shoutout action config */
  shoutoutActionConfig?: Record<string, any> | null;
}

export interface NotifyByAppResponse {
  /** notification_batch_id */
  notificationBatchId?: string;
}

export interface ResolveRequest {
  /**
   * template id
   * @format GUID
   */
  notificationTemplateId?: string;
  /** values */
  dynamicValues?: Record<string, DynamicValue>;
}

export interface ResolveResponse {}
